import { Component, h } from 'preact'
import Table from '../table/Table'
import ButtonControl from '../button-control'
import UserAvatar from '../User/UserAvatar'
import { Member } from './GroupEdit'
import axios from 'axios'
import formatDateInLocalTimezone from '../utils/dateFormatting'
import ConfirmationModal from '../ui-components/ConfirmationModal'

interface Props {
  groupId: number,
  groupName: string
}

interface State {
  members: null | Array<Member>,
  deleteInProgress: boolean,
  showConfirmation: boolean,
  userToDelete: null | Member
}

export class GroupMembers extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      members: null,
      deleteInProgress: false,
      showConfirmation: false,
      userToDelete: null
    }

    this.onRemove = this.onRemove.bind(this)
    this.getDeleteConfirmationTitle = this.getDeleteConfirmationTitle.bind(this)
    this.getDeleteConfirmationText = this.getDeleteConfirmationText.bind(this)
    this.getDeleteConfirmationButtons = this.getDeleteConfirmationButtons.bind(this)
    this.closeConfirmationDialog = this.closeConfirmationDialog.bind(this)

    let element: HTMLInputElement | null = document.querySelector('input#csrftoken')

    if (element !== null) {
      this.csrf = element.value
    }
  }

  csrf: string = ''

  async onRemove () {
    this.setState({ deleteInProgress: true })
    const userId = this.state.userToDelete?.id
    await axios.delete(`/platform/group/${this.props.groupId}/members/${userId}`, { data: { csrfToken: this.csrf } })
    const idx = (this.state.members || []).findIndex(member => member.id === userId)
    const newList: Array<Member> = (this.state.members || []).concat([])
    newList.splice(idx, 1)
    this.setState({ members: newList, userToDelete: null, deleteInProgress: false, showConfirmation: false })
  }

  closeConfirmationDialog () {
    this.setState({ showConfirmation: false })
  }

  getDeleteConfirmationTitle () {
    return `Remove ${this.state.userToDelete?.username} from ${this.props.groupName}?`
  }

  getDeleteConfirmationText () {
    return <div>
      If you remove a member, he or she can no longer post in this group
    </div>
  }

  getDeleteConfirmationButtons () {
    return [
      {
        text: 'Delete',
        callback: () => this.onRemove(),
        class: 'btn btn-danger',
        showLoading: true
      },
      {
        text: 'Cancel',
        callback: this.closeConfirmationDialog,
        class: 'btn btn-default',
        showLoading: false
      }
    ]
  }

  async componentDidMount () {
    const response = await axios.get(`/platform/group/${this.props.groupId}/members`)
    if (response.data) {
      this.setState({
        members: response.data
      })
    }
  }

  render (_: Props, { members, deleteInProgress, showConfirmation }: State) {
    const MembersTableModel = [{
      header: 'User',
      accessor: 'title'
    }, {
      header: 'Role',
      accessor: 'role'
    }, {
      header: 'Rank title',
      accessor: 'rank_title'
    }, {
      header: 'Member since',
      accessor: 'member_since'
    }, {
      header: 'Actions',
      accessor: 'actions'
    }]
    const MembersTableRows = (members || []).map((member: Member) => (
      {
        title: <div className='user-info'>
          <div className='user-info_avatar'>
            <UserAvatar user={{ ...member, name: member.username }} openInNewTab style={{ width: '40px', height: '40px' }} />
          </div>
          <div className='user-info_username-email'>
            <div className='user-info_username'>
              { member.username }
            </div>
            <div className='user-info_email'>{ member.email }</div>
          </div>
        </div>,
        role: <div>{ member.member_status === 'group_admin' ? 'Admin' : 'Member' }</div>,
        rank_title: <div>{ member.rank || '-' }</div>,
        member_since: <div>{ formatDateInLocalTimezone(member.member_since) }</div>,
        actions: <div>
          {
            member.member_status !== 'group_admin' ? (
              <ButtonControl title='Remove' variant='warning' onClick={() => this.setState({ userToDelete: member, showConfirmation: true })} />
            ) : null
          }
        </div>
      }
    ))

    if (members === null) {
      return null
    }

    return (
      <div className='group-members-tab'>
        {
          members.length ? (
            <div>
              <div className='group-users-management_header'>
                <strong>Members ({members.length})</strong> <a href={`/platform/group/${this.props.groupId}/members.csv`}>Export CSV</a>
              </div>
              <Table model={MembersTableModel} rows={MembersTableRows} narrow />
              <ConfirmationModal
                isLoading={deleteInProgress}
                enableModal={showConfirmation}
                title={this.getDeleteConfirmationTitle()}
                message={this.getDeleteConfirmationText()}
                buttons={this.getDeleteConfirmationButtons()}
                onClose={this.closeConfirmationDialog}
              />
            </div>
          ) : null
        }
      </div>
    )
  }
}

export default GroupMembers
